import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "@/lib/api";
import { adminKeys } from "@/lib/query";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

type Stat = { label: string; value: number; href: string };
type Activity = {
  when: string;
  who: string;
  kind: "order" | "member" | "retreat";
  what: string;
  amount?: string;
};

const DOT = ["#d4606a", "#606b4c", "#c9934c", "#f0a0a3"];
const SCRAP_BG = [colors.cream, colors.blushLight, colors.lilacLight, colors.warmCream];
const PILL: Record<Activity["kind"], { bg: string; fg: string; bd: string }> = {
  order: { bg: "rgba(96,107,76,0.10)", fg: "#606b4c", bd: "rgba(96,107,76,0.20)" },
  member: { bg: "rgba(240,160,163,0.18)", fg: "#a5454f", bd: "rgba(212,96,106,0.25)" },
  retreat: { bg: "rgba(201,147,76,0.12)", fg: "#8a5f1e", bd: "rgba(201,147,76,0.28)" },
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

export default function AdminDashboard() {
  const overviewQuery = useQuery({
    queryKey: adminKeys.overview(),
    queryFn: () => api.get<{ stats: Stat[]; recent: Activity[] }>("/api/admin/overview"),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const stats = overviewQuery.data?.stats ?? [];
  const recent = overviewQuery.data?.recent ?? [];

  return (
    <section>
      <style>{css}</style>
      <div className="dash-header">
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h1 className="aflt-admin-title">Dashboard</h1>
          <svg width="160" height="10" viewBox="0 0 160 10" fill="none" aria-hidden>
            <path
              d="M2 6 C10 2, 18 9, 26 5 C34 1, 42 8, 50 5 C58 2, 66 8, 74 5 C82 2, 90 8, 98 5 C106 2, 114 8, 122 5 C130 2, 138 8, 146 5 C152 2, 156 7, 158 6"
              stroke={colors.tan}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeDasharray="4 3"
              opacity={0.65}
            />
          </svg>
        </div>
        <Link to="/admin/products" className="btn-woven">
          + New
        </Link>
      </div>

      {overviewQuery.isLoading ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <div className="stat-row">
            {stats.map((s, i) => (
              <Link key={s.label} to={s.href} className={`stat-card scrap-${i % 4}`}>
                <div className="stat-eyebrow">{s.label}</div>
                <div className="stat-number">{s.value}</div>
                <span className="stat-dot" style={{ background: DOT[i % 4], opacity: 0.5 }} />
              </Link>
            ))}
          </div>

          {recent.length > 0 && (
            <div className="activity-card">
              <div className="activity-head">
                <span className="activity-title">Recent Activity</span>
                <Link to="/admin/orders" className="activity-all">
                  See all →
                </Link>
              </div>
              <table className="activity-table">
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>When</th>
                    <th style={{ width: 210 }}>Who</th>
                    <th>What</th>
                    <th style={{ width: 110, textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((a, i) => (
                    <tr key={i}>
                      <td>
                        <span className="when">{a.when}</span>
                      </td>
                      <td>
                        <div className="who">
                          <span className="mini-avatar" style={{ background: DOT[i % 4] }}>
                            {initials(a.who)}
                          </span>
                          {a.who}
                        </div>
                      </td>
                      <td>
                        <span
                          className="what-pill"
                          style={{ background: PILL[a.kind].bg, color: PILL[a.kind].fg, borderColor: PILL[a.kind].bd }}
                        >
                          {a.what}
                        </span>
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{a.amount ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

const css = `
  .dash-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 32px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; text-decoration: none; outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px;
    box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .stat-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 32px; }
  .stat-card { display: block; padding: 22px 22px 20px; border-radius: 14px; border: 1.5px dashed rgba(201,147,76,0.40); position: relative; text-decoration: none; color: inherit; transition: transform 0.18s, box-shadow 0.18s; }
  .stat-card:hover { transform: translateY(-2px) rotate(0deg) !important; box-shadow: 0 10px 24px -14px rgba(8,45,61,0.30); }
  .scrap-0 { background: ${SCRAP_BG[0]}; transform: rotate(-0.5deg); box-shadow: 2px 3px 10px rgba(201,147,76,0.10); }
  .scrap-1 { background: ${SCRAP_BG[1]}; transform: rotate(0.4deg); box-shadow: 2px 3px 10px rgba(212,96,106,0.08); }
  .scrap-2 { background: ${SCRAP_BG[2]}; transform: rotate(-0.3deg); box-shadow: 2px 3px 10px rgba(201,147,76,0.09); }
  .scrap-3 { background: ${SCRAP_BG[3]}; transform: rotate(0.5deg); box-shadow: 2px 3px 10px rgba(96,107,76,0.08); }
  .stat-eyebrow { font-family: ${fonts.body}; font-size: 10px; font-weight: 700; letter-spacing: 0.20em; text-transform: uppercase; color: rgba(8,45,61,0.47); margin-bottom: 8px; }
  .stat-number { font-family: ${fonts.logo}; font-size: 36px; font-weight: 500; color: ${colors.navy}; line-height: 1; }
  .stat-dot { width: 8px; height: 8px; border-radius: 50%; position: absolute; top: 14px; right: 16px; }
  .activity-card { background: #fff; border-radius: 16px; border: 1.5px dashed rgba(201,147,76,0.40); padding: 26px 28px 18px; box-shadow: 0 2px 12px rgba(8,45,61,0.05); }
  .activity-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
  .activity-title { font-family: ${fonts.body}; font-size: 10px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: rgba(8,45,61,0.73); }
  .activity-all { font-family: ${fonts.body}; font-size: 12px; font-weight: 600; color: ${colors.berryRose}; text-decoration: none; }
  .activity-table { width: 100%; border-collapse: collapse; }
  .activity-table thead th { font-family: ${fonts.body}; font-size: 10px; font-weight: 700; letter-spacing: 0.20em; text-transform: uppercase; color: rgba(8,45,61,0.47); text-align: left; padding: 0 0 12px; border-bottom: 1px solid rgba(201,147,76,0.15); }
  .activity-table tbody td { padding: 13px 0; font-family: ${fonts.body}; font-size: 13.5px; font-weight: 500; color: rgba(8,45,61,0.73); border-bottom: 1px dashed rgba(201,147,76,0.15); vertical-align: middle; }
  .activity-table tbody tr:last-child td { border-bottom: none; }
  .who { display: flex; align-items: center; gap: 9px; }
  .mini-avatar { width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-family: ${fonts.logo}; font-size: 11px; font-weight: 600; color: #fff; }
  .what-pill { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; border: 1px solid transparent; }
  .when { font-family: ${fonts.body}; font-size: 12px; color: rgba(8,45,61,0.47); white-space: nowrap; }
`;
